/**
 * NatureHome — /nature (Global Nature Atlas hub)
 * Rich category cards with a hero image, short caption and a colorful
 * category "stamp" badge. Fetches everything in one round-trip via
 * `/api/nature/atlas/category-cards`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Camera, Leaf, Bird, Mountain, Waves, ArrowRight, Sparkles,
  Trees, Flower2, Fish, Bug, Butterfly, Sun, Bug as Spider,
  Cloud, Snowflake, Compass, TreePine, Wheat, Cherry, Carrot,
  Shell, Egg, Rat, MapPin, Route, Milestone, Bike, Waves as WavesIcon,
  Landmark, Home, HeartHandshake, Camera as CameraIcon,
  Users, Globe2, ShieldCheck,
} from "lucide-react";
import { http } from "@/lib/api";

// One expressive "stamp" per category: icon + gradient background.
// Used both on the hero photo overlay and as a fallback when a
// category has no photo yet.
const STAMP = {
  // plants
  trees:               { icon: TreePine,  gradient: "linear-gradient(135deg,#065f46,#22c55e)", emoji: "🌳" },
  flowers:             { icon: Flower2,   gradient: "linear-gradient(135deg,#be185d,#fb7185)", emoji: "🌸" },
  shrubs:              { icon: Leaf,      gradient: "linear-gradient(135deg,#166534,#84cc16)", emoji: "🌿" },
  herbs:               { icon: Leaf,      gradient: "linear-gradient(135deg,#365314,#a3e635)", emoji: "🌱" },
  medicinal:           { icon: Leaf,      gradient: "linear-gradient(135deg,#134e4a,#5eead4)", emoji: "🌾" },
  crops:               { icon: Wheat,     gradient: "linear-gradient(135deg,#a16207,#facc15)", emoji: "🌾" },
  fruits:              { icon: Cherry,    gradient: "linear-gradient(135deg,#be123c,#fda4af)", emoji: "🍎" },
  vegetables:          { icon: Carrot,    gradient: "linear-gradient(135deg,#c2410c,#fdba74)", emoji: "🥕" },
  mushrooms:           { icon: Sun,       gradient: "linear-gradient(135deg,#7c2d12,#fb923c)", emoji: "🍄" },
  // animals
  mammals:             { icon: Rat,       gradient: "linear-gradient(135deg,#78350f,#f59e0b)", emoji: "🦁" },
  birds:               { icon: Bird,      gradient: "linear-gradient(135deg,#1e3a8a,#60a5fa)", emoji: "🦅" },
  fish:                { icon: Fish,      gradient: "linear-gradient(135deg,#0369a1,#38bdf8)", emoji: "🐟" },
  marine:              { icon: Shell,     gradient: "linear-gradient(135deg,#155e75,#22d3ee)", emoji: "🐙" },
  reptiles:            { icon: Egg,       gradient: "linear-gradient(135deg,#166534,#a3e635)", emoji: "🦎" },
  amphibians:          { icon: Bug,       gradient: "linear-gradient(135deg,#14532d,#4ade80)", emoji: "🐸" },
  insects:             { icon: Bug,       gradient: "linear-gradient(135deg,#713f12,#eab308)", emoji: "🐛" },
  butterflies:         { icon: Sparkles,  gradient: "linear-gradient(135deg,#6b21a8,#c084fc)", emoji: "🦋" },
  pollinators:         { icon: Bug,       gradient: "linear-gradient(135deg,#a16207,#fde047)", emoji: "🐝" },
  spiders:             { icon: Spider,    gradient: "linear-gradient(135deg,#1e293b,#64748b)", emoji: "🕷️" },
  wildlife:            { icon: Rat,       gradient: "linear-gradient(135deg,#78350f,#fbbf24)", emoji: "🦌" },
  // places
  forests:             { icon: TreePine,  gradient: "linear-gradient(135deg,#14532d,#22c55e)", emoji: "🌲" },
  mountains:           { icon: Mountain,  gradient: "linear-gradient(135deg,#334155,#94a3b8)", emoji: "⛰️" },
  rivers:              { icon: WavesIcon, gradient: "linear-gradient(135deg,#0c4a6e,#38bdf8)", emoji: "🏞️" },
  lakes:               { icon: WavesIcon, gradient: "linear-gradient(135deg,#075985,#7dd3fc)", emoji: "🏞️" },
  ponds:               { icon: WavesIcon, gradient: "linear-gradient(135deg,#365314,#84cc16)", emoji: "💧" },
  wetlands:            { icon: WavesIcon, gradient: "linear-gradient(135deg,#166534,#4ade80)", emoji: "🌾" },
  waterfalls:          { icon: WavesIcon, gradient: "linear-gradient(135deg,#0369a1,#7dd3fc)", emoji: "🏞️" },
  beaches:             { icon: WavesIcon, gradient: "linear-gradient(135deg,#a16207,#fbbf24)", emoji: "🏖️" },
  islands:             { icon: WavesIcon, gradient: "linear-gradient(135deg,#0c4a6e,#22d3ee)", emoji: "🏝️" },
  deserts:             { icon: Sun,       gradient: "linear-gradient(135deg,#a16207,#fde047)", emoji: "🏜️" },
  caves:               { icon: Mountain,  gradient: "linear-gradient(135deg,#292524,#78716c)", emoji: "🕳️" },
  parks:               { icon: Trees,     gradient: "linear-gradient(135deg,#166534,#4ade80)", emoji: "🏞️" },
  "botanical-gardens": { icon: Flower2,   gradient: "linear-gradient(135deg,#be185d,#f472b6)", emoji: "🌷" },
  "national-parks":    { icon: TreePine,  gradient: "linear-gradient(135deg,#14532d,#22c55e)", emoji: "🏞️" },
  "wildlife-sanctuaries":{ icon: Bird,    gradient: "linear-gradient(135deg,#7c2d12,#f97316)", emoji: "🦌" },
  "nature-reserves":   { icon: Leaf,      gradient: "linear-gradient(135deg,#166534,#84cc16)", emoji: "🌲" },
  "hiking-trails":     { icon: Route,     gradient: "linear-gradient(135deg,#7c2d12,#f97316)", emoji: "🥾" },
  "walking-trails":    { icon: Route,     gradient: "linear-gradient(135deg,#78350f,#f59e0b)", emoji: "🚶" },
  "cycling-trails":    { icon: Bike,      gradient: "linear-gradient(135deg,#7c3aed,#c084fc)", emoji: "🚴" },
  promenades:          { icon: Milestone, gradient: "linear-gradient(135deg,#a16207,#fbbf24)", emoji: "🌆" },
  "scenic-drives":     { icon: Compass,   gradient: "linear-gradient(135deg,#7c2d12,#fdba74)", emoji: "🚗" },
  geological:          { icon: Mountain,  gradient: "linear-gradient(135deg,#292524,#a8a29e)", emoji: "🪨" },
  // systems
  climate:             { icon: Cloud,     gradient: "linear-gradient(135deg,#1e3a8a,#60a5fa)", emoji: "🌦️" },
  ecosystems:          { icon: Leaf,      gradient: "linear-gradient(135deg,#134e4a,#22d3ee)", emoji: "🌍" },
  conservation:        { icon: ShieldCheck,gradient: "linear-gradient(135deg,#065f46,#34d399)", emoji: "🛡️" },
  photography:         { icon: CameraIcon,gradient: "linear-gradient(135deg,#1e293b,#94a3b8)", emoji: "📸" },
  "citizen-science":   { icon: Users,     gradient: "linear-gradient(135deg,#7c3aed,#c084fc)", emoji: "🔬" },
};

const GROUP_ICON = { plants: Leaf, animals: Bird, places: Mountain, systems: Waves };

function _stamp(cat) { return STAMP[cat] || { icon: Leaf, gradient: "linear-gradient(135deg,#334155,#94a3b8)", emoji: "🌿" }; }

export default function NatureHome() {
  const [meta, setMeta]         = useState(null);
  const [cards, setCards]       = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const m = await http.get("/nature/atlas/meta").then(r => r.data);
        setMeta(m);
      } catch { setMeta({ groups: [] }); }
      try {
        const c = await http.get("/nature/atlas/category-cards").then(r => r.data);
        setCards(c.items || []);
      } catch { setCards([]); }
      try {
        const f = await http.get("/nature/atlas/entities?limit=8&has_body=true").then(r => r.data);
        setFeatured(f.items || []);
      } catch { setFeatured([]); }
    })();
  }, []);

  const cardsByCategory = useMemo(() => {
    const map = {};
    cards.forEach((c) => { map[c.category] = c; });
    return map;
  }, [cards]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="nature-home">
      {/* Hero */}
      <section className="mb-8">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>🌿 NATURE PULSE</div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
          Discover. Explore. Identify. Protect.
        </h1>
        <p className="text-base md:text-lg opacity-80 max-w-3xl mb-4">
          Nature Pulse is Social Pulse's community-powered <strong>Global Nature Atlas</strong> —
          a living record of every plant, animal, ecosystem and natural
          place on Earth, built together with citizen scientists, educators
          and explorers like you.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link to="/nature/welcome" className="wofy-btn-ghost" data-testid="nature-explore-more">
            Explore the vision <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/nature/identify" className="wofy-btn-primary" data-testid="nature-identify-cta">
            <Camera className="w-4 h-4" /> Identify with AI
          </Link>
          <Link to="/nature/observations" className="wofy-btn-ghost" data-testid="nature-observations-cta">
            <Sparkles className="w-4 h-4" /> Community observations
          </Link>
        </div>
      </section>

      {/* Themed groups → rich category cards */}
      {(meta?.groups || []).map((g) => {
        const Icon = GROUP_ICON[g.slug] || Leaf;
        return (
          <section key={g.slug} className="mb-10" data-testid={`nature-group-${g.slug}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="overline mb-1 inline-flex items-center gap-1" style={{ color: "var(--wofy-primary)" }}>
                  <Icon className="w-3.5 h-3.5" /> {g.label.toUpperCase()}
                </div>
                <p className="text-sm opacity-70">{g.hint}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.categories.map((c) => (
                <CategoryCard key={c.slug} category={c} card={cardsByCategory[c.slug]} />
              ))}
            </div>
          </section>
        );
      })}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display text-2xl mb-3">Featured species & places</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="nature-featured">
            {featured.map((it) => (
              <Link
                key={it.slug}
                to={`/nature/species/${it.slug}`}
                data-testid={`nature-featured-${it.slug}`}
                className="rounded-xl border overflow-hidden hover:shadow-md transition"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {it.photo_url && (
                  <div className="aspect-video bg-black/5">
                    <img src={it.photo_url} alt={it.name}
                         className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-3">
                  <div className="font-medium leading-tight">{it.name}</div>
                  {it.scientific_name && (
                    <div className="text-[11px] font-mono italic opacity-70 mt-0.5">{it.scientific_name}</div>
                  )}
                  {it.tagline && (
                    <p className="text-xs opacity-80 mt-1 line-clamp-2">{it.tagline}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}


function CategoryCard({ category, card }) {
  const stamp = _stamp(category.slug);
  const Icon  = stamp.icon;
  const count = card?.count ?? 0;
  const hero  = card?.hero_photo;
  const caption = card?.hero_caption || category.hint || "";
  const heroName = card?.hero_name;

  return (
    <Link
      to={`/nature/atlas/${category.slug}`}
      data-testid={`nature-cat-${category.slug}`}
      className="group rounded-2xl overflow-hidden border transition hover:shadow-md flex flex-col"
      style={{ borderColor: "var(--wofy-border)" }}
    >
      {/* Hero image with stamp overlay */}
      <div className="relative aspect-[16/10] overflow-hidden"
           style={{ background: stamp.gradient }}>
        {hero ? (
          <img src={hero} alt={heroName || category.label}
               className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
               loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl opacity-40">
            {stamp.emoji}
          </div>
        )}
        {/* Colorful stamp badge — top-left */}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-[11px] font-mono uppercase tracking-wider shadow"
             style={{ background: stamp.gradient }}>
          <Icon className="w-3 h-3" />
          <span>{category.kind}</span>
        </div>
        {/* Species count — top-right */}
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-white/90 shadow">
          {count} <span className="opacity-60">entries</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <span className="text-lg">{stamp.emoji}</span>
          <div className="font-medium leading-tight group-hover:underline">{category.label}</div>
        </div>
        {caption && (
          <p className="text-xs opacity-80 mt-1 line-clamp-2">
            {heroName && <span className="font-medium opacity-90">{heroName}: </span>}
            {caption}
          </p>
        )}
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] opacity-60 group-hover:opacity-100"
             style={{ color: "var(--wofy-primary)" }}>
          Explore {category.label.toLowerCase()} <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}
